import { useCallback, useContext, useState } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import { colors } from "@dotnet/telematics-design-system";

import { DEVICE_ACTIONS, DialogProps } from "components/Actions/types";
import Autocomplete from "components/DesignComponents/Autocomplete";
import Dialog from "components/DesignComponents/Dialog";
import SampleTemplate from "components/DesignComponents/SampleTemplate";
import Dropzone from "components/Dropzone";
import HighlightedOption from "components/HighlightedOption";
import AlertContext from "context/alert/alertContext";
import AuthContext from "context/auth/authContext";
import DropzoneContext from "context/dropzone/dropzoneContext";
import ParamsContext from "context/params/paramsContext";
import {
    BackgroundActionData,
    DROPDOWN_SIZES,
    ENDPOINT_PATH_SEGMENTS,
    ENDPOINTS,
    maxDialogWidth,
    TABLE_NAMES,
    TRI_SOURCES,
    useApi,
    useMediaQueries
} from "shared";

import DialogActions from "../../components/DialogActions";
import { FILE_NAME } from "../types";
import { ATTRIBUTES } from "./types";

const ImportData = ({ isOpen, close }: DialogProps) => {
    const { textPlaceholder } = colors;

    const { removeAlertForBGAction, setBackgroundActionId } =
        useContext(AlertContext);

    const { rootCompany, setReloadItems } = useContext(ParamsContext);
    const { isSupportUser } = useContext(AuthContext);
    const { file, lineCount, removeAllFiles } = useContext(DropzoneContext);

    const { t } = useTranslation();
    const { postData, handleResponse } = useApi();
    const { toMd, fromMd } = useMediaQueries();

    const [inputValue, setInputValue] = useState("");
    const [selectedAttribute, setSelectedAttribute] = useState<null | string>(
        null
    );

    const [isLoading, setIsLoading] = useState(false);

    const isReady = selectedAttribute && file && lineCount > 0;

    const handleChangeAttribute = (attr: string) => setSelectedAttribute(attr);

    const getFormData = useCallback(() => {
        const formData = new FormData();

        if (selectedAttribute) {
            formData.append("source", TRI_SOURCES.FromFile);
            formData.append("attribute", selectedAttribute);
            formData.append("file", file as Blob);

            isSupportUser &&
                formData.append("root_id", String(rootCompany?.id));
        }

        return formData;
    }, [file, selectedAttribute, isSupportUser, rootCompany?.id]);

    const handleCloseDialog = useCallback(() => {
        close(DEVICE_ACTIONS.ImportDevices);
    }, [close]);

    const handleSubmit = useCallback(async () => {
        removeAlertForBGAction();

        try {
            setIsLoading(true);

            const { data }: { data: BackgroundActionData } = await postData(
                `${ENDPOINTS.Devices}${ENDPOINT_PATH_SEGMENTS.AttributeUpdate}`,
                getFormData()
            );

            setBackgroundActionId(
                data,
                TABLE_NAMES.Devices,
                () => {
                    handleCloseDialog();
                    setReloadItems(TABLE_NAMES.Devices);
                },
                () => setIsLoading(false)
            );
        } catch (error) {
            handleResponse(error);
            setIsLoading(false);
        }
    }, [
        removeAlertForBGAction,
        getFormData,
        handleCloseDialog,
        setReloadItems,
        handleResponse,
        postData,
        setBackgroundActionId
    ]);

    const limits = {
        [ATTRIBUTES.Description]: 255,
        [ATTRIBUTES.GsmNumber]: 20,
        [ATTRIBUTES.ICCID]: 22,
        [ATTRIBUTES.IMSI]: 15
    };

    return (
        <Dialog
            data-testid="import-device-data-dialog"
            title={t("Dialog##import device data")}
            description={t("Dialog##import device data description")}
            isTitleSeparator
            isActionsSeparator
            open={isOpen}
            close={isLoading ? undefined : handleCloseDialog}
            submit={handleSubmit}
            slotProps={{
                transition: {
                    onExited: () => {
                        removeAlertForBGAction();
                        setIsLoading(false);
                        removeAllFiles();
                        setSelectedAttribute(null);
                    }
                }
            }}
            actions={
                <DialogActions
                    onClose={handleCloseDialog}
                    isLoading={isLoading}
                    loaderText={t("Button##importing")}
                    isReadyToConfirm={isReady}
                    confirmationButtonText={t("Button##import")}
                    cancelButtonTestId="cancel-import-data"
                    confirmationButtonTestId="import-data-button"
                />
            }
            css={css(fromMd && maxDialogWidth)}
            extendToMaxWidth={toMd}
        >
            <Dropzone
                supportedFormats={[".csv"]}
                countCsv
                isLoading={isLoading}
                tooltipText={`
                        ${t("Dropzone##file syntax")} 
                        ${t("Dialog##imei")},
                        ${t("Dialog##attribute value")}
                    `}
            />

            <SampleTemplate fileName={FILE_NAME.SampleImportList} />

            <Autocomplete
                data-testid="device-attributes-dropdown"
                isRequired
                labelIcon={t("Dialog##import device data info")}
                options={Object.values(ATTRIBUTES)}
                getOptionLabel={option => t(`Dialog##${option}`)}
                renderOption={(propsData, option: ATTRIBUTES) => {
                    const { key, ...restProps } = propsData;

                    return (
                        <li key={key} {...restProps}>
                            <span>
                                <HighlightedOption
                                    text={t(`Dialog##${option}`)}
                                    query={inputValue}
                                />
                            </span>

                            <span
                                css={css({
                                    color: textPlaceholder,
                                    marginLeft: "5px"
                                })}
                            >
                                {t(`Dialog##import device data limit`, {
                                    limit: limits[option]
                                })}
                            </span>
                        </li>
                    );
                }}
                size={DROPDOWN_SIZES.Medium}
                fullWidth
                onInputChange={(_, newInputValue) =>
                    setInputValue(newInputValue)
                }
                onChange={(_, val) => handleChangeAttribute(val)}
                labelLeft={t("Table##attribute")}
                readOnly={isLoading}
                textFieldParams={{
                    placeholder: t("Dialog##select")
                }}
            />
        </Dialog>
    );
};
export default ImportData;
